/**
 * Finanzas → **Kardex de combustibles** (`/finanzas/kardex`).
 *
 * Puerto de `Kardex::index` + su modelo con la vista `views/kardex/index.php` y el
 * bundle `src/kardex/index.js`.
 *
 * Se elige el **producto** (una manguera de `pts_fuel_grades`), la **valorización** y
 * el **rango de fechas**, y la API devuelve el libro ya armado: cada entrada, salida,
 * venta del POS y ajuste con su **saldo** y su **valorizado** al final de la fila.
 *
 * Reglas heredadas:
 * - Dos valorizaciones: **costo promedio móvil** (el costo se recalcula con cada
 *   entrada) y **costo fijo** (el precio actual del combustible para todo el período).
 * - Las **calibraciones** del POS figuran como salida pero no mueven el saldo: son
 *   pruebas de la manguera.
 * - El saldo inicial es todo lo anterior al rango (entradas − salidas − ventas +
 *   ajustes).
 * - La visualización se puede cambiar a **consolidado** (miles y millones abreviados),
 *   y la preferencia se guarda en el navegador, como en el original.
 *
 * Diferencias anotadas:
 * - El **cálculo lo hace la API** una sola vez (el original lo tenía duplicado: una
 *   copia en el controlador para el Excel y otra en el JavaScript para la tabla).
 * - El rango por defecto es el **mes en curso** con atajos (hoy, este mes, el mes
 *   anterior); el original abría en «hoy» y, sin datos de hoy, salía en blanco.
 * - El aviso de que el promedio móvil puede empezar en cero se mantiene, pero ahora
 *   se ve como nota al pie en lugar del bloque largo de indicaciones.
 */

import { Download, Info, Layers, Search, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency } from '@/lib/utils';

import { KardexAjusteDialog } from '../components/kardex-ajuste-dialog';
import { kardexApi, useLibroKardex, useProductosKardex } from '../kardex.api';
import type { FilaKardex, FiltrosKardex, Valorizacion } from '../kardex.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Clave de la preferencia de visualización (la misma que usaba el bundle). */
const CLAVE_VISUALIZACION = 'kardex_display_mode';

type Visualizacion = 'decimals' | 'compact';

/** `AAAA-MM-DDTHH:mm` de un `Date`, con la hora local. */
function enCampo(fecha: Date): string {
  const dia = String(fecha.getDate()).padStart(2, '0');
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}T${String(fecha.getHours()).padStart(2, '0')}:${String(
    fecha.getMinutes(),
  ).padStart(2, '0')}`;
}

/** `AAAA-MM-DD HH:mm:ss` a partir del valor del campo de fecha. */
function enApi(valor: string): string {
  return `${valor.replace('T', ' ')}:00`;
}

/** Primer y último día del mes que contiene la fecha. */
function mesDe(fecha: Date): { desde: string; hasta: string } {
  const primero = new Date(fecha.getFullYear(), fecha.getMonth(), 1, 0, 0, 0);
  const ultimo = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0, 23, 59, 0);

  return { desde: enCampo(primero), hasta: enCampo(ultimo) };
}

/**
 * Número abreviado (15.2K, 1.5M) para la visualización consolidada, como el original.
 */
function compacto(valor: number, conMoneda = false): string {
  const signo = valor < 0 ? '-' : '';
  const abs = Math.abs(valor);

  let escalado = abs;
  let sufijo = '';

  if (abs >= 1_000_000_000) {
    escalado = abs / 1_000_000_000;
    sufijo = 'B';
  } else if (abs >= 1_000_000) {
    escalado = abs / 1_000_000;
    sufijo = 'M';
  } else if (abs >= 1000) {
    escalado = abs / 1000;
    sufijo = 'K';
  }

  const decimales = escalado >= 100 ? 0 : escalado >= 10 ? 1 : 2;
  const texto = escalado
    .toFixed(decimales)
    .replace(/\.0+$/, '')
    .replace(/(\.\d*[1-9])0+$/, '$1');

  return conMoneda ? `${signo}S/ ${texto}${sufijo}` : `${signo}${texto}${sufijo}`;
}

/** Cantidad con tres decimales (la visualización por defecto). */
function conDecimales(valor: number | '' | null | undefined): string {
  const numero = Number(valor ?? 0);

  return Number.isFinite(numero)
    ? numero.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : '0.000';
}

export function KardexPage() {
  useDocumentTitle('Finanzas · Kardex de combustibles');

  const [productoId, setProductoId] = useState('');
  const [valorizacion, setValorizacion] = useState<Valorizacion>('moving_average');
  const [rango, setRango] = useState(() => mesDe(new Date()));
  const [filtros, setFiltros] = useState<FiltrosKardex | null>(null);
  const [visualizacion, setVisualizacion] = useState<Visualizacion>(() => {
    const guardada = localStorage.getItem(CLAVE_VISUALIZACION);

    return guardada === 'compact' ? 'compact' : 'decimals';
  });
  const [dialogo, setDialogo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);

  const productos = useProductosKardex();
  const libro = useLibroKardex(filtros);

  const lista = useMemo(() => productos.data?.productos ?? [], [productos.data]);

  // El producto elegido: por defecto el primero del catálogo.
  const elegido = useMemo(
    () => lista.find((producto) => String(producto.Id) === productoId) ?? lista[0],
    [lista, productoId],
  );

  useEffect(() => {
    localStorage.setItem(CLAVE_VISUALIZACION, visualizacion);
  }, [visualizacion]);

  const consultar = () => {
    setError(null);
    setAviso(null);

    if (!elegido) {
      setError('No hay productos configurados para el kardex.');
      return;
    }

    if (rango.desde > rango.hasta) {
      setError('La fecha de inicio no puede ser posterior a la de fin.');
      return;
    }

    setFiltros({
      fuel_grade_id: elegido.Id,
      product_id: elegido.producto_id,
      start_datetime: enApi(rango.desde),
      end_datetime: enApi(rango.hasta),
      valuation_method: valorizacion,
    });
  };

  const datos = libro.data;

  // El libro trae el saldo inicial como primera fila y luego los movimientos del rango.
  // El saldo inicial se ancla arriba de la tabla (siempre visible) y los movimientos se
  // paginan en el navegador: un mes de un producto puede traer cientos de renglones.
  // Los totales del pie se siguen calculando sobre el listado completo, no sobre la página.
  const filas = useMemo(() => datos?.filas ?? [], [datos]);
  const inicial = useMemo(() => filas.filter((fila) => fila.tipo === 'SALDO INICIAL'), [filas]);
  const movimientosDelRango = useMemo(
    () => filas.filter((fila) => fila.tipo !== 'SALDO INICIAL'),
    [filas],
  );
  const paginacion = usePaginacion(movimientosDelRango, { clave: filtros });

  const mostrarCantidad = (valor: number | '' | null | undefined) => {
    const numero = Number(valor ?? 0);

    if (visualizacion === 'compact' && Math.abs(numero) >= 1000) return compacto(numero);

    return conDecimales(valor);
  };

  const mostrarPrecio = (valor: number | '' | null | undefined) => {
    if (valor === '' || valor === null || valor === undefined) return '—';

    const numero = Number(valor);

    return visualizacion === 'compact' && Math.abs(numero) >= 1000
      ? compacto(numero, true)
      : formatCurrency(numero);
  };

  const exportar = async () => {
    if (!filtros) return;

    setError(null);
    setDescargando(true);

    try {
      await abrirPdfAutenticado(kardexApi.urlExcel(filtros), {
        nombre: `kardex_${datos?.producto ?? 'producto'}_${filtros.start_datetime.slice(0, 10).replace(/-/g, '')}.xlsx`,
      });
    } catch (fallo) {
      setError(
        fallo instanceof ApiError
          ? fallo.message
          : fallo instanceof Error
            ? fallo.message
            : 'No se pudo exportar el kardex.',
      );
    } finally {
      setDescargando(false);
    }
  };

  /** Totales del pie: sumas de las columnas de importe de las filas del período. */
  const totales = (() => {
    const filas = (datos?.filas ?? []).filter((fila) => fila.tipo !== 'SALDO INICIAL');

    return filas.reduce(
      (acumulado, fila) => ({
        entradas: acumulado.entradas + Number(fila.totalEntrada || 0),
        salidas: acumulado.salidas + Number(fila.totalSalida || 0),
      }),
      { entradas: 0, salidas: 0 },
    );
  })();

  const esCalibracion = (fila: FilaKardex) => fila.tipo === 'CALIBRACIÓN';

  /** Una fila del libro: la misma pintura para el saldo inicial y para cada movimiento. */
  const pintarFila = (fila: FilaKardex, indice: number) => (
    <tr
      key={`${fila.tipo}-${fila.fecha}-${indice}`}
      className={cn(
        'border-t border-ink-100',
        fila.tipo === 'SALDO INICIAL' && 'bg-ink-50/70 font-semibold',
        esCalibracion(fila) && 'bg-amber-50/40 text-ink-500',
      )}
    >
      <td className="px-2 py-1.5 whitespace-nowrap">{fila.fecha}</td>
      <td className="max-w-[16rem] truncate px-2 py-1.5" title={fila.documento}>
        {fila.documento}
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-semibold',
            fila.tipo === 'ENTRADA' && 'bg-emerald-50 text-emerald-700',
            fila.tipo === 'SALIDA' && 'bg-amber-50 text-amber-700',
            fila.tipo === 'VENTAS' && 'bg-rose-50 text-rose-700',
            fila.tipo === 'CALIBRACIÓN' && 'bg-ink-100 text-ink-600',
            fila.tipo === 'AJUSTE' && 'bg-sky-50 text-sky-700',
            fila.tipo === 'SALDO INICIAL' && 'bg-brand-50 text-brand-700',
          )}
        >
          {fila.tipo}
        </span>
      </td>
      <td className="px-2 py-1.5 text-center tabular-nums">{fila.turno || '—'}</td>

      <td className="border-l border-ink-100 px-2 py-1.5 text-right tabular-nums">
        {fila.cantidadEntrada > 0 ? mostrarCantidad(fila.cantidadEntrada) : '—'}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{mostrarPrecio(fila.precioEntrada)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        {fila.totalEntrada === '' ? '—' : mostrarPrecio(fila.totalEntrada)}
      </td>

      <td className="border-l border-ink-100 px-2 py-1.5 text-right tabular-nums">
        {fila.cantidadSalida > 0 ? mostrarCantidad(fila.cantidadSalida) : '—'}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{mostrarPrecio(fila.precioSalida)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        {fila.totalSalida === '' ? '—' : mostrarPrecio(fila.totalSalida)}
      </td>

      <td className="border-l border-ink-100 px-2 py-1.5 text-right font-semibold tabular-nums">
        {mostrarCantidad(fila.saldoCantidad)}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{mostrarPrecio(fila.saldoPrecio)}</td>
      <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
        {mostrarPrecio(fila.saldoTotal)}
      </td>
    </tr>
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Kardex de combustibles</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los movimientos de un producto —entradas, salidas, ventas del POS y ajustes— con su
            saldo y su valorización.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            leftIcon={<SlidersHorizontal className="size-4" aria-hidden="true" />}
            disabled={!elegido}
            onClick={() => setDialogo(true)}
          >
            Ajuste
          </Button>
          <Button
            variant="outline"
            leftIcon={<Download className="size-4" aria-hidden="true" />}
            disabled={!filtros}
            loading={descargando}
            onClick={() => void exportar()}
          >
            Exportar Excel
          </Button>
        </div>
      </header>

      {error ? (
        <Alert variant="error" title="Revisa los filtros">
          {error}
        </Alert>
      ) : null}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Producto
              <Select
                value={elegido ? String(elegido.Id) : ''}
                onChange={(evento) => setProductoId(evento.target.value)}
                className={CONTROL}
                aria-label="Producto"
              >
                {lista.length === 0 ? <option value="">Sin productos</option> : null}
                {lista.map((producto) => (
                  <option key={producto.Id} value={String(producto.Id)}>
                    {producto.Name} · {formatCurrency(producto.Price)}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Valorización
              <Select
                value={valorizacion}
                onChange={(evento) => setValorizacion(evento.target.value as Valorizacion)}
                className={CONTROL}
                aria-label="Valorización"
              >
                <option value="moving_average">Costo promedio móvil</option>
                <option value="fixed">Costo fijo (precio del combustible)</option>
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Desde
              <CampoFecha
                tipo="datetime-local"
                value={rango.desde}
                onChange={(evento) => setRango((actual) => ({ ...actual, desde: evento.target.value }))}
                aria-label="Desde"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Hasta
              <CampoFecha
                tipo="datetime-local"
                value={rango.hasta}
                onChange={(evento) => setRango((actual) => ({ ...actual, hasta: evento.target.value }))}
                aria-label="Hasta"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button leftIcon={<Search className="size-4" aria-hidden="true" />} onClick={consultar}>
              Consultar
            </Button>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRango(mesDe(new Date()))}
              >
                Este mes
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const hoy = new Date();
                  const anterior = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);

                  setRango(mesDe(anterior));
                }}
              >
                Mes anterior
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const hoy = new Date();
                  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 0, 0, 0);
                  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 0);

                  setRango({ desde: enCampo(primero), hasta: enCampo(ultimo) });
                }}
              >
                Hoy
              </Button>
            </div>

            <div className="ml-auto flex items-center gap-1 rounded-control border border-ink-200 p-0.5">
              {(
                [
                  ['decimals', '3 decimales'],
                  ['compact', 'Consolidado'],
                ] as [Visualizacion, string][]
              ).map(([clave, etiqueta]) => (
                <button
                  key={clave}
                  type="button"
                  className={cn(
                    'rounded-[6px] px-2.5 py-1 text-xs font-medium transition-colors',
                    visualizacion === clave
                      ? 'bg-brand-500 text-white'
                      : 'text-ink-600 hover:bg-ink-50',
                  )}
                  onClick={() => setVisualizacion(clave)}
                >
                  {etiqueta}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contadores */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[
          { etiqueta: 'Saldo inicial', valor: mostrarCantidad(datos?.saldo_inicial), tono: 'text-ink-900' },
          { etiqueta: 'Entradas', valor: mostrarCantidad(datos?.total_entradas), tono: 'text-emerald-700' },
          { etiqueta: 'Salidas', valor: mostrarCantidad(datos?.total_salidas), tono: 'text-amber-700' },
          { etiqueta: 'Ventas', valor: mostrarCantidad(datos?.total_ventas), tono: 'text-rose-700' },
          { etiqueta: 'Saldo final', valor: mostrarCantidad(datos?.saldo_final), tono: 'text-ink-900' },
          {
            etiqueta: 'Saldo valorizado',
            valor: datos ? formatCurrency(datos.saldo_valorizado) : formatCurrency(0),
            tono: 'text-sky-700',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="flex flex-col gap-0.5 p-4">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className={cn('text-lg font-bold tabular-nums', tarjeta.tono)}>{tarjeta.valor}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Libro */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                  valorizacion === 'moving_average'
                    ? 'border-amber-200 bg-amber-50 text-amber-700'
                    : 'border-brand-200 bg-brand-50 text-brand-700',
                )}
              >
                {valorizacion === 'moving_average' ? 'Costo promedio móvil' : 'Costo fijo'}
              </span>
              {datos ? (
                <span className="text-xs text-ink-500">
                  {datos.producto} · {datos.desde} a {datos.hasta} ·{' '}
                  {(datos.movimientos ?? 0).toLocaleString('es-PE')} movimiento(s)
                </span>
              ) : null}
            </div>
          </div>

          {libro.isError ? (
            <Alert variant="error" title="No se pudo consultar el kardex">
              {libro.error instanceof ApiError
                ? libro.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-ink-50/70">
                <tr className="text-center text-[11px] font-semibold tracking-wide text-ink-600 uppercase">
                  <th rowSpan={2} className="px-2 py-2 text-left">
                    Fecha/Hora
                  </th>
                  <th rowSpan={2} className="px-2 py-2 text-left">
                    Documento
                  </th>
                  <th rowSpan={2} className="px-2 py-2 text-left">
                    Tipo
                  </th>
                  <th rowSpan={2} className="px-2 py-2">
                    Turno
                  </th>
                  <th colSpan={3} className="border-l border-ink-200 px-2 py-1 text-emerald-700">
                    Entradas
                  </th>
                  <th colSpan={3} className="border-l border-ink-200 px-2 py-1 text-amber-700">
                    Salidas
                  </th>
                  <th colSpan={3} className="border-l border-ink-200 px-2 py-1 text-sky-700">
                    Saldo final
                  </th>
                </tr>
                <tr className="text-right text-[11px] font-semibold text-ink-500 uppercase">
                  {['Cantidad', 'Precio', 'Total', 'Cantidad', 'Precio', 'Total', 'Cantidad', 'Precio', 'Total'].map(
                    (titulo, indice) => (
                      <th
                        key={`${titulo}-${indice}`}
                        className={cn(
                          'px-2 py-1 whitespace-nowrap',
                          [0, 3, 6].includes(indice) && 'border-l border-ink-200',
                        )}
                      >
                        {titulo}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {libro.isLoading
                  ? Array.from({ length: 6 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 13 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : [
                      ...inicial.map((fila, indice) => pintarFila(fila, indice)),
                      ...paginacion.visibles.map((fila, indice) => pintarFila(fila, indice)),
                    ]}
              </tbody>
              {filas.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-ink-200 bg-ink-50/60 text-[12px] font-semibold text-ink-700">
                    <td className="px-2 py-2" colSpan={4}>
                      Totales del período
                    </td>
                    <td className="border-l border-ink-200 px-2 py-2 text-right tabular-nums">
                      {mostrarCantidad(datos?.total_entradas)}
                    </td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatCurrency(totales.entradas)}
                    </td>
                    <td className="border-l border-ink-200 px-2 py-2 text-right tabular-nums">
                      {mostrarCantidad(datos?.total_salidas)}
                    </td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatCurrency(totales.salidas)}
                    </td>
                    <td className="border-l border-ink-200 px-2 py-2 text-right tabular-nums">
                      {mostrarCantidad(datos?.saldo_final)}
                    </td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatCurrency(datos?.saldo_valorizado ?? 0)}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          <Paginador {...paginacion} etiqueta="movimientos" />

          {!libro.isLoading && filtros === null ? (
            <p className="text-sm text-ink-500">
              Elige el producto, la valorización y el rango de fechas, y pulsa <em>Consultar</em>.
            </p>
          ) : null}

          {!libro.isLoading && filtros !== null && filas.length === 0 ? (
            <Alert variant="warning" title="Sin movimientos en el rango">
              Ese producto no tiene entradas, salidas, ventas ni ajustes entre las fechas elegidas.
            </Alert>
          ) : null}

          <div className="flex items-start gap-2 text-xs text-ink-500">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              Las <strong>calibraciones</strong> del surtidor se muestran como salida pero no mueven
              el saldo: son pruebas de la manguera. En <strong>costo promedio móvil</strong> el costo
              se recalcula con cada entrada y las salidas se valorizan a ese costo; si no hay
              entradas con precio, se parte del precio actual del combustible.
            </span>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Layers className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        La visualización <strong>consolidada</strong> abrevia los miles y millones (15.2K, 1.5M) y
        se guarda en este navegador.
      </p>

      <KardexAjusteDialog
        open={dialogo}
        onClose={() => setDialogo(false)}
        producto={elegido}
        valorizacion={valorizacion}
        onAviso={(mensaje) => {
          setAviso(mensaje);
          setFiltros(null);
        }}
      />
    </div>
  );
}
